import { Inject, Injectable } from '@angular/core';
import { Router, ROUTER_CONFIGURATION } from '@angular/router';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { RegimeApiModel } from '../models/regime-api-model';
import { RegisterService } from '../services/register.service';
import {
  navigateToRouteWithReplacedParameters,
  TypedActivatedRouteSnapshot,
} from '../utils/route-utils';
import { RegisterData, RegisterParams } from './register.resolver';

export type RegimeParams = RegisterParams & {
  regimeId: string;
};

export type RegimeData = RegisterData & {
  regime: RegimeApiModel;
};

@Injectable({
  providedIn: 'root',
})
export class RegimeResolver {
  private static _regimes$: (Observable<RegimeApiModel[]> & { registerId?: number }) | undefined;

  public static clearCache() {
    RegimeResolver._regimes$ = undefined;
  }

  private getRegimes(registerId: number) {
    if (!RegimeResolver._regimes$ || RegimeResolver._regimes$.registerId !== registerId) {
      RegimeResolver._regimes$ = this.registerService.getRegimes(registerId).pipe(shareReplay());
    }

    RegimeResolver._regimes$.registerId = registerId;
    return RegimeResolver._regimes$;
  }

  constructor(
    private router: Router,
    private registerService: RegisterService,
    @Inject(ROUTER_CONFIGURATION) private routerConfig: { enableTracing: boolean },
  ) {}

  resolve(route: TypedActivatedRouteSnapshot<RegimeParams>): Observable<RegimeApiModel> {
    const registerId = +route.params.registerId;

    if (!registerId) {
      throw new Error('No registerId param');
    }

    const regimePath = route.params.regimeId || '';
    const regimeId = +regimePath.split('-')[0];

    return this.getRegimes(registerId).pipe(
      map(regimes => {
        let regime = regimes.find(r => r.id === regimeId);

        if (regime) {
          if (this.routerConfig.enableTracing) {
            console.log('Regime Resolver: resolved', regime);
          }
          sessionStorage.setItem('regimeName', regime.name);
          localStorage.setItem('regimeName', regime.name);
        } else {
          // Try to get regime from session
          let regimeName = sessionStorage.getItem('regimeName') || '';
          regime = regimes.find(r => r.name === regimeName);

          if (!regime) {
            // Else try to get regime from local storage
            // Else fall back to default - includes when changing countries
            regimeName = localStorage.getItem('regimeName') || '';

            regime = regimes.find(r => r.name === regimeName) || regimes[0];
          }

          if (!regime) {
            throw new Error('No regimes found');
          }

          if (this.routerConfig.enableTracing) {
            console.log('Regime Resolver: redirecting', regime);
          }
          navigateToRouteWithReplacedParameters(
            this.router,
            {
              regimeId: regime.id,
            },
            route,
          );
        }

        return regime;
      }),
    );
  }
}
